// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Button,
  Column,
  Description,
  InlineAlert,
  InputCheckbox,
  InputText,
  MdPadding,
  MdSpacing,
  XlPadding,
  XsSpacing,
} from '@alto/design-system';
import { isValid, parse } from 'date-fns';
import React from 'react';
import { useController, useForm } from 'react-hook-form';
import { PHARMACY_NOTICES_URL, PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '~shared/constants';
import { DATE_FORMATS, formatDateInput, getAge } from '~shared/helpers/date';
import { getErrorMessage } from '~shared/helpers/helper';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { useClaimAccountViewed } from '~shared/lib/analytics/src/hooks/useClaimAccountViewed';
import { useSelectorShared } from '~shared/store';
import { Phone } from '~web/components/Phone';

export type Values = {
  date_of_birth: string;
  terms: boolean;
};

const initialValues = {
  date_of_birth: '',
  terms: false,
};

export type VerifyProps = {
  readonly disabled?: boolean;
  readonly onVerify: (userValues: Values) => Promise<void>;
  readonly excludeHeaderImage?: boolean;
  readonly tempPassword?: string;
};

export const Verify = ({ onVerify, disabled, tempPassword }: VerifyProps) => {
  useClaimAccountViewed(tempPassword ?? '');
  const validatingUserError = useSelectorShared((state) => state.ui.errors.validatingUserError);
  const verifyLoading = useSelectorShared((state) => state.ui.loading.validatingUserLoading);
  const infoRequestFlow = window.location.pathname.startsWith('/info/');

  const { control, handleSubmit, watch, trigger } = useForm<Values>({
    defaultValues: initialValues,
    mode: 'onSubmit',
  });

  const dateOfBirthController = useController({
    control,
    name: 'date_of_birth',
    rules: {
      validate: (value: string) => {
        if (value === '') {
          return 'Please enter a valid date of birth in the MM/DD/YYYY format';
        }

        // Strict regex: two-digit month (01-12), two-digit day (01-31), four-digit year (0000-9999)
        const dateRegex = /^(0[1-9]|1[0-2]) \/ (0[1-9]|[12]\d|3[01]) \/ (\d{4})$/;

        if (!dateRegex.test(value)) {
          return 'Please enter a valid date of birth in the MM/DD/YYYY format';
        }

        // Extract components to validate individually
        const parts = value.split(' / ');
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Validate year is 4 digits (not 2 digits) and not in the future
        const currentYear = new Date().getFullYear();
        if (year > currentYear) {
          return 'Please enter a date before today';
        }

        // Parse and validate the full date (this will catch invalid days like Feb 30)
        const parsedDate = parse(value, DATE_FORMATS.DATE_INPUT_FORMAT, new Date(0));

        if (!isValid(parsedDate)) {
          return 'Please enter a valid date of birth in the MM/DD/YYYY format';
        }

        // Check that the parsed date components match what was entered (prevents date-fns from auto-correcting)
        const parsedMonth = parsedDate.getMonth() + 1; // getMonth() returns 0-11
        const parsedDay = parsedDate.getDate();
        const parsedYear = parsedDate.getFullYear();

        if (parsedMonth !== month || parsedDay !== day || parsedYear !== year) {
          return 'Please enter a valid date of birth in the MM/DD/YYYY format';
        }

        return true;
      },
    },
  });

  const termsController = useController({
    control,
    name: 'terms',
    rules: {
      validate: (value: boolean) => {
        return value || "Please indicate that you've read and accept the terms above";
      },
    },
  });

  const dateOfBirth = watch('date_of_birth');
  const age = getAge(dateOfBirth);
  const termsCheckError = termsController.fieldState.error?.message;

  return (
    <Column>
      <InputText
        label="Date of birth"
        aria-label="Enter your date of birth here in numbers, for example as 02 28 1991"
        placeholder={DATE_FORMATS.DATE_INPUT_PLACEHOLDER}
        {...mapControllerToInputProps(dateOfBirthController)}
        onChangeText={(value) => {
          dateOfBirthController.field.onChange(formatDateInput(value));
        }}
        onBlur={() => {
          dateOfBirthController.field.onBlur();
          trigger('date_of_birth');
        }}
        required
      />
      {!infoRequestFlow && !!age && age < 18 ? (
        <>
          <MdPadding
            leftPadding={SPACING.STATIC.NONE}
            rightPadding={SPACING.STATIC.NONE}
          >
            <InlineAlert>
              <Description fontFamily="semibold">Are you under 18?</Description>
              <Description>If so, please have your parent or guardian give us a call at {<Phone />}.</Description>
            </InlineAlert>
          </MdPadding>
        </>
      ) : null}
      <XlPadding
        leftPadding={SPACING.STATIC.NONE}
        rightPadding={SPACING.STATIC.NONE}
      >
        <InputCheckbox
          testID="terms-checkbox"
          name="terms"
          label=""
          fullBleed
          value={termsController.field.value}
          onPress={() => {
            termsController.field.onChange(!termsController.field.value);
          }}
          noHighlight
          descriptions={
            <Description>
              I have read and accept the{' '}
              <a
                href={TERMS_OF_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              ,{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              , and{' '}
              <a
                href={PHARMACY_NOTICES_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Pharmacy Notices
              </a>
              .
            </Description>
          }
        />
        {termsCheckError ? (
          <>
            <XsSpacing />
            <Description color={COLORS.TEXT_COLORS.DANGER}>{termsCheckError}</Description>
          </>
        ) : null}
      </XlPadding>
      <Button
        disabled={disabled}
        loading={verifyLoading}
        loadingLabel="Loading..."
        label="Verify"
        onPress={async () => {
          const isValid = await trigger();
          if (isValid) {
            handleSubmit(onVerify)();
          }
        }}
        width="full"
      />
      {validatingUserError ? (
        <>
          <MdSpacing />
          <InlineAlert type="error">
            <Description>
              {getErrorMessage(validatingUserError) || 'Verification failed - please check your info and try again.'}
            </Description>
          </InlineAlert>
        </>
      ) : null}
    </Column>
  );
};
